import { Button, Group, Stack, Table, Text, Title } from "@mantine/core";
import { IconBriefcase } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  EXPLAIN_WITH_AI_EMPTY,
  EXPLAIN_WITH_AI_FAILED,
  EXPLAIN_WITH_AI_LABEL,
  EXPLAIN_WITH_AI_LOADING,
  FIT_REASON_FULL_COUNT,
  FIT_REASON_PREVIEW_COUNT,
  LOG_CONTACT_LABEL,
  MATCH_TABLE_ACTION,
  MATCH_TABLE_CONTACT,
  MATCH_TABLE_FIT,
  MATCH_TABLE_JOB,
  MATCH_TABLE_WHY,
  MATCHED_JOBS_DESCRIPTION,
  MATCHED_JOBS_EMPTY_DESCRIPTION,
  MATCHED_JOBS_EMPTY_TITLE,
  MATCHED_JOBS_ERROR_TITLE,
  MATCHED_JOBS_TITLE,
} from "../../constants/matching";
import { candidatesApi, queueApi } from "../../lib/api";
import { ContentCard, DataTableShell, ErrorState, useVisibleWindow } from "../ui";
import { ContactStatus } from "./ContactStatus";
import { FitReasonList } from "./FitReasonList";
import { FitTierBadge } from "./FitTierBadge";
import { LogContactModal } from "./LogContactModal";
import { SUBMIT_TO_JOB_LABEL } from "../SubmitCandidateModal";
import type { TopJobMatch } from "../../types/api";

type RationaleState = {
  loading: boolean;
  text: string | null;
  error: boolean;
};

export function MatchedJobsSection({
  candidateId,
  onSubmit,
}: {
  candidateId: string;
  onSubmit: (jobId: string) => void;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [logJobId, setLogJobId] = useState<string | null>(null);
  const [rationales, setRationales] = useState<Record<string, RationaleState>>({});
  const query = useQuery({
    queryKey: ["candidate-matches", candidateId],
    queryFn: () => candidatesApi.matches(candidateId),
    enabled: !!candidateId,
  });
  const matches = query.data ?? [];
  const matchWindow = useVisibleWindow(matches, candidateId);

  async function handleExplain(matchId: string) {
    setExpanded(matchId);
    const current = rationales[matchId];
    if (current?.loading || (current && !current.error && current.text)) return;
    setRationales((prev) => ({
      ...prev,
      [matchId]: { loading: true, text: current?.text ?? null, error: false },
    }));
    try {
      const result = await queueApi.rationale(matchId);
      const text = result.rationale?.trim() ? result.rationale : EXPLAIN_WITH_AI_EMPTY;
      setRationales((prev) => ({
        ...prev,
        [matchId]: { loading: false, text, error: false },
      }));
    } catch {
      setRationales((prev) => ({
        ...prev,
        [matchId]: { loading: false, text: null, error: true },
      }));
    }
  }

  if (query.isError) {
    return (
      <ContentCard>
        <Stack gap="md">
          <Stack gap="var(--axio-unit-6)">
            <Title order={5}>{MATCHED_JOBS_TITLE}</Title>
            <Text size="sm" c="dimmed">
              {MATCHED_JOBS_DESCRIPTION}
            </Text>
          </Stack>
          <ErrorState
            title={MATCHED_JOBS_ERROR_TITLE}
            message={query.error instanceof Error ? query.error.message : undefined}
            onRetry={() => void query.refetch()}
          />
        </Stack>
      </ContentCard>
    );
  }

  return (
    <ContentCard>
      <Stack gap="md">
        <Stack gap="var(--axio-unit-6)">
          <Title order={5}>{MATCHED_JOBS_TITLE}</Title>
          <Text size="sm" c="dimmed">
            {MATCHED_JOBS_DESCRIPTION}
          </Text>
        </Stack>
        <DataTableShell
          withBorder={false}
          loading={query.isLoading}
          hasMore={matchWindow.hasMore}
          loadingMore={matchWindow.loadingMore}
          onLoadMore={matchWindow.loadMore}
          isEmpty={matches.length === 0}
          empty={{
            icon: IconBriefcase,
            title: MATCHED_JOBS_EMPTY_TITLE,
            description: MATCHED_JOBS_EMPTY_DESCRIPTION,
          }}
        >
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{MATCH_TABLE_JOB}</Table.Th>
                <Table.Th>{MATCH_TABLE_FIT}</Table.Th>
                <Table.Th>{MATCH_TABLE_WHY}</Table.Th>
                <Table.Th>{MATCH_TABLE_CONTACT}</Table.Th>
                <Table.Th>{MATCH_TABLE_ACTION}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {matchWindow.items.map((match) => (
                <MatchJobRow
                  key={match.match_id}
                  match={match}
                  expanded={expanded === match.match_id}
                  rationale={rationales[match.match_id]}
                  onToggle={() => setExpanded(expanded === match.match_id ? null : match.match_id)}
                  onSubmit={() => onSubmit(match.job_id)}
                  onLog={() => setLogJobId(match.job_id)}
                  onExplain={() => void handleExplain(match.match_id)}
                />
              ))}
            </Table.Tbody>
          </Table>
        </DataTableShell>
      </Stack>
      <LogContactModal
        opened={!!logJobId}
        onClose={() => setLogJobId(null)}
        candidateId={candidateId}
        jobId={logJobId}
      />
    </ContentCard>
  );
}

function MatchJobRow({
  match,
  expanded,
  rationale,
  onToggle,
  onSubmit,
  onLog,
  onExplain,
}: {
  match: TopJobMatch;
  expanded: boolean;
  rationale?: RationaleState;
  onToggle: () => void;
  onSubmit: () => void;
  onLog: () => void;
  onExplain: () => void;
}) {
  return (
    <>
      <Table.Tr onClick={onToggle} style={{ cursor: "pointer" }}>
        <Table.Td>
          <Stack gap="var(--axio-unit-2)">
            <Text component={Link} to={`/jobs/${match.job_id}`} fw="var(--axio-font-weight-semibold)" size="sm">
              {match.job_title}
            </Text>
            <Text size="xs" c="dimmed">
              {match.company_name}
            </Text>
          </Stack>
        </Table.Td>
        <Table.Td>
          <FitTierBadge tier={match.fit_tier} score={match.fit_score} />
        </Table.Td>
        <Table.Td>
          <FitReasonList reasons={match.fit_reasons} limit={FIT_REASON_PREVIEW_COUNT} />
        </Table.Td>
        <Table.Td>
          <ContactStatus lastContactedAt={match.last_contacted_at} lastContactOutcome={match.last_contact_outcome} />
        </Table.Td>
        <Table.Td>
          <Group gap="xs" wrap="nowrap" onClick={(event) => event.stopPropagation()}>
            <Button size="xs" variant="default" onClick={onLog}>
              {LOG_CONTACT_LABEL}
            </Button>
            <Button size="xs" variant="default" onClick={onSubmit}>
              {SUBMIT_TO_JOB_LABEL}
            </Button>
            <Button size="xs" variant="default" onClick={onExplain}>
              {EXPLAIN_WITH_AI_LABEL}
            </Button>
          </Group>
        </Table.Td>
      </Table.Tr>
      {expanded ? (
        <Table.Tr>
          <Table.Td colSpan={5}>
            <Stack gap="md">
              <FitReasonList reasons={match.fit_reasons} limit={FIT_REASON_FULL_COUNT} />
              {rationale?.loading ? (
                <Text size="sm" c="dimmed">
                  {EXPLAIN_WITH_AI_LOADING}
                </Text>
              ) : null}
              {rationale?.error ? (
                <Text size="sm" c="dimmed">
                  {EXPLAIN_WITH_AI_FAILED}
                </Text>
              ) : null}
              {rationale?.text && !rationale.loading ? (
                <Text size="sm">{rationale.text}</Text>
              ) : null}
            </Stack>
          </Table.Td>
        </Table.Tr>
      ) : null}
    </>
  );
}
